import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { KeyboardTimePicker } from '@material-ui/pickers'
import { unwrapResult } from '@reduxjs/toolkit'
import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useState } from 'react'
import { apiTimeFormat } from 'shared/constants/AppConst'
import { apiStatus } from 'shared/constants/AppEnum'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import {
    offeringListThunk,
    offeringRetrieveThunk,
    offeringTimeAddThunk,
    offeringTimeDeleteThunk,
    offeringTimeUpdateThunk,
    selectOfferingTimeStatus
} from 'store/reducers/temple/offeringSlice'
import { offeringPoolCreateThunk, offeringPoolUpdateThunk } from 'store/reducers/temple/offeringPoolSlice'
import { IOfferingResponse } from 'types/api/temple'
import { BlockCalcEnum, IOfferingFormTimeType } from 'types/store/temple'
import { extractError, getEditedPayload } from 'utils/api'
import { formToOfferingTime, getTimeChipText, offeringTimeToForm } from 'utils/temple/offering'
import PoolSelect from './PoolSelect'

type OfferingTimeDraftType = {
    fromTime: Date | ''
    toTime: Date | ''
    blockCalcChooser: BlockCalcEnum
    maxLimit: number | ''
    index: number | ''
    id?: string | number
    url?: string
    poolConnections: string[]
}

interface OfferingTimeManagerDialogProps {
    open: boolean
    offerings: IOfferingResponse[]
    mode: 'single' | 'bulk'
    onClose: () => void
    onComplete?: () => void
}

const getEmptyDraft = (blockCalcChooser: BlockCalcEnum = BlockCalcEnum.FROM): OfferingTimeDraftType => ({
    fromTime: '',
    toTime: '',
    blockCalcChooser,
    maxLimit: '',
    index: '',
    id: '',
    url: '',
    poolConnections: []
})

const OfferingTimeManagerDialog: React.FC<OfferingTimeManagerDialogProps> = ({ open, offerings, mode, onClose, onComplete }) => {
    const dispatch = useAppDispatch()
    const api = useAppSelector(selectOfferingTimeStatus)
    const { enqueueSnackbar } = useSnackbar()
    const [times, setTimes] = useState<IOfferingFormTimeType[]>([])
    const [draft, setDraft] = useState<OfferingTimeDraftType>(getEmptyDraft())
    const [currentOfferingTime, setCurrentOfferingTime] = useState<IOfferingFormTimeType>()
    const [isApplyingBulk, setIsApplyingBulk] = useState(false)
    const [openPools, setOpenPools] = useState(false)
    const offering = offerings[0]
    const isBulk = mode === 'bulk'

    useEffect(() => {
        if (!open) return
        if (isBulk) {
            setTimes([])
        } else {
            setTimes(offering?.offeringTimes?.map(time => offeringTimeToForm(time)) ?? [])
        }
        setDraft(getEmptyDraft())
        setCurrentOfferingTime(undefined)
        setOpenPools(false)
    }, [open, isBulk, offering])

    const title = useMemo(() => {
        if (isBulk) return `Add offering times to ${offerings.length} offerings`
        return offering ? `Edit offering times - ${offering.name}` : 'Edit offering times'
    }, [isBulk, offering, offerings.length])

    const areTimesSame = () =>
        draft.fromTime &&
        draft.toTime &&
        draft.fromTime.toTimeString() === draft.toTime.toTimeString()

    const isButtonDisabled = () => Boolean(
        !draft.fromTime ||
        areTimesSame() ||
        (draft.toTime && draft.toTime <= draft.fromTime)
    )

    const toOfferingFormTime = (current: OfferingTimeDraftType): IOfferingFormTimeType => ({
        id: current.id,
        url: current.url,
        fromTime: current.fromTime as Date,
        toTime: current.toTime,
        blockCalcChooser: current.blockCalcChooser,
        maxLimit: current.maxLimit,
        poolConnections: current.poolConnections
    })

    const resetDraft = () => {
        setDraft(getEmptyDraft(draft.blockCalcChooser))
        setCurrentOfferingTime(undefined)
    }

    const handleChipClick = (time: IOfferingFormTimeType, index: number) => {
        setCurrentOfferingTime(time)
        setDraft({
            fromTime: time.fromTime,
            toTime: time.toTime,
            blockCalcChooser: time.blockCalcChooser,
            maxLimit: time.maxLimit,
            index,
            id: time.id,
            url: time.url,
            poolConnections: time.poolConnections
        })
    }

    const hasDuplicateTime = (timeToAdd: IOfferingFormTimeType) => times.some((time, index) => {
        if (draft.index !== '' && draft.index === index) return false
        return (
            time.fromTime.toTimeString() === timeToAdd.fromTime.toTimeString() &&
            Boolean(time.toTime) === Boolean(timeToAdd.toTime) &&
            (!time.toTime || !timeToAdd.toTime || time.toTime.toTimeString() === timeToAdd.toTime.toTimeString())
        )
    })

    const createPoolForTime = async (targetOffering: IOfferingResponse, time: IOfferingFormTimeType) => {
        const from = format(time.fromTime.setSeconds(0, 0), apiTimeFormat)
        const to = time.toTime ? format(time.toTime.setSeconds(0, 0), apiTimeFormat) : ''
        return dispatch(offeringPoolCreateThunk({
            poolName: `${targetOffering.name}_${from}-${to}_pool`,
            poolLimit: time.maxLimit ? time.maxLimit.toString() : undefined
        })).then(unwrapResult)
    }

    const withPoolConnections = async (targetOffering: IOfferingResponse, time: IOfferingFormTimeType) => {
        if (time.poolConnections.length > 0) return time
        const pool = await createPoolForTime(targetOffering, time)
        return { ...time, poolConnections: [pool.url] }
    }

    const handleAdd = async () => {
        const offeringTime = toOfferingFormTime(draft)
        if (hasDuplicateTime(offeringTime)) {
            enqueueSnackbar('This time already exists', { variant: 'warning' })
            return
        }

        if (isBulk) {
            setTimes(prev => [...prev, offeringTime])
            resetDraft()
            return
        }

        if (!offering) return
        try {
            const timeWithPool = await withPoolConnections(offering, offeringTime)
            const response = await dispatch(offeringTimeAddThunk({
                url: offering.url,
                data: formToOfferingTime(timeWithPool)
            })).then(unwrapResult)
            setTimes(prev => [...prev, offeringTimeToForm(response)])
            await dispatch(offeringRetrieveThunk({ id: offering.id }))
            enqueueSnackbar('Offering time added', { variant: 'success' })
            resetDraft()
            onComplete?.()
        } catch (err: any) {
            enqueueSnackbar(extractError(err) ?? 'Failed to add offering time', { variant: 'error' })
        }
    }

    const handleUpdate = async () => {
        if (draft.index === '') return
        const offeringTime = toOfferingFormTime(draft)
        if (hasDuplicateTime(offeringTime)) {
            enqueueSnackbar('This time already exists', { variant: 'warning' })
            return
        }

        if (isBulk) {
            setTimes(prev => prev.map((time, index) => index === draft.index ? offeringTime : time))
            resetDraft()
            return
        }

        if (!offering || !currentOfferingTime?.url) return
        const payload = getEditedPayload(formToOfferingTime(currentOfferingTime), formToOfferingTime(offeringTime))
        if (payload.poolConnections) {
            payload.poolConnections = formToOfferingTime(offeringTime).poolConnections
        }
        if (Object.keys(payload).length === 0) {
            resetDraft()
            return
        }

        try {
            if (payload.maxLimit && draft.poolConnections.length < 2 && draft.poolConnections[0]) {
                await dispatch(offeringPoolUpdateThunk({
                    url: draft.poolConnections[0],
                    data: { poolLimit: payload.maxLimit.toString() }
                })).then(unwrapResult)
            }
            const response = await dispatch(offeringTimeUpdateThunk({
                url: currentOfferingTime.url,
                data: payload
            })).then(unwrapResult)
            setTimes(prev => prev.map((time, index) => index === draft.index ? offeringTimeToForm(response) : time))
            await dispatch(offeringRetrieveThunk({ id: offering.id }))
            enqueueSnackbar('Offering time updated', { variant: 'success' })
            resetDraft()
            onComplete?.()
        } catch (err: any) {
            enqueueSnackbar(extractError(err) ?? 'Failed to update offering time', { variant: 'error' })
        }
    }

    const handleDelete = async () => {
        if (draft.index === '') return

        if (isBulk) {
            setTimes(prev => prev.filter((_, index) => index !== draft.index))
            resetDraft()
            return
        }

        if (!offering || !draft.url) return
        try {
            await dispatch(offeringTimeDeleteThunk({ url: draft.url })).then(unwrapResult)
            setTimes(prev => prev.filter((_, index) => index !== draft.index))
            await dispatch(offeringRetrieveThunk({ id: offering.id }))
            enqueueSnackbar('Offering time deleted', { variant: 'success' })
            resetDraft()
            onComplete?.()
        } catch (err: any) {
            enqueueSnackbar(extractError(err) ?? 'Failed to delete offering time', { variant: 'error' })
        }
    }

    const handleBulkApply = async () => {
        if (!times.length || !offerings.length) return

        let successCount = 0
        let failedCount = 0
        setIsApplyingBulk(true)
        for (const targetOffering of offerings) {
            for (const time of times) {
                try {
                    const timeWithPool = await withPoolConnections(targetOffering, time)
                    await dispatch(offeringTimeAddThunk({
                        url: targetOffering.url,
                        data: formToOfferingTime(timeWithPool)
                    })).then(unwrapResult)
                    successCount += 1
                } catch (err) {
                    failedCount += 1
                }
            }
        }
        setIsApplyingBulk(false)
        await dispatch(offeringListThunk({}))
        onComplete?.()
        if (failedCount > 0) {
            enqueueSnackbar(`${successCount} time entries added, ${failedCount} failed`, { variant: 'warning' })
        } else {
            enqueueSnackbar(`${successCount} time entries added`, { variant: 'success' })
        }
        onClose()
    }

    const isEditing = draft.index !== ''
    const isLoading = api.status === apiStatus.LOADING || isApplyingBulk

    return (
        <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {isBulk && (
                    <Box mb={4}>
                        <Typography variant="body2">Add one or more time entries, then apply them to the selected offerings.</Typography>
                    </Box>
                )}
                <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                        <KeyboardTimePicker
                            label="From"
                            onChange={time => setDraft(prev => ({ ...prev, fromTime: time ?? '' }))}
                            value={draft.fromTime === '' ? null : draft.fromTime}
                            mask="__:__ _M"
                            inputVariant="outlined"
                            size="small"
                            placeholder="hh:mm AM/PM"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <KeyboardTimePicker
                            label="To"
                            onChange={time => setDraft(prev => ({ ...prev, toTime: time ?? '' }))}
                            value={draft.toTime === '' ? null : draft.toTime}
                            mask="__:__ _M"
                            inputVariant="outlined"
                            size="small"
                            placeholder="hh:mm AM/PM"
                        />
                    </Grid>
                    {draft.poolConnections.length < 2 && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Max Limit"
                                type="number"
                                variant="outlined"
                                size="small"
                                value={draft.maxLimit}
                                onChange={event => setDraft(prev => ({
                                    ...prev,
                                    maxLimit: event.target.value === '' ? '' : Number(event.target.value)
                                }))}
                                fullWidth
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Choose</FormLabel>
                            <RadioGroup
                                row
                                name="blockCalcChooser"
                                value={draft.blockCalcChooser}
                                onChange={event => setDraft(prev => ({ ...prev, blockCalcChooser: event.target.value as BlockCalcEnum }))}
                            >
                                <FormControlLabel value={BlockCalcEnum.FROM} control={<Radio />} label="From Time" />
                                <FormControlLabel value={BlockCalcEnum.TO} control={<Radio />} label="To Time" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            onClick={() => setOpenPools(prev => !prev)}
                            variant="outlined"
                            size="small"
                        >
                            {openPools ? 'Close Pools' : `Choose Pools${draft.poolConnections.length ? ` (${draft.poolConnections.length})` : ''}`}
                        </Button>
                    </Grid>
                    {openPools && (
                        <Grid item xs={12}>
                            <PoolSelect
                                pools={draft.poolConnections}
                                setPools={(pools) => setDraft(prev => ({ ...prev, poolConnections: pools }))}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {isEditing ? (
                            <Box display="flex" gridGap={12}>
                                <Button onClick={handleUpdate} disabled={isButtonDisabled() || isLoading} variant="outlined" color="primary">Update</Button>
                                <Button onClick={handleDelete} disabled={isLoading} variant="outlined" color="secondary">Delete</Button>
                                <Button onClick={resetDraft} disabled={isLoading} variant="outlined">Cancel</Button>
                            </Box>
                        ) : (
                            <Button onClick={handleAdd} disabled={isButtonDisabled() || isLoading} variant="outlined">Add</Button>
                        )}
                    </Grid>
                    {isLoading && (
                        <Grid item xs={12}>
                            <LinearProgress />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {times.length > 0 ? times.map((time, index) => (
                            <Chip
                                key={`${time.url ?? 'new'}-${index}`}
                                label={getTimeChipText(time)}
                                color="primary"
                                variant="outlined"
                                onClick={() => handleChipClick(time, index)}
                                style={{ margin: '4px 8px 4px 0' }}
                            />
                        )) : (
                            <Typography variant="body2" color="textSecondary">No offering times added.</Typography>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>Close</Button>
                {isBulk && (
                    <Button onClick={handleBulkApply} disabled={isLoading || times.length === 0} color="primary" variant="contained">
                        Apply to Selected
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default OfferingTimeManagerDialog
